import { useParams, Link } from 'react-router-dom';
import { getArticleById, getHotArticles } from '@/mocks/articles';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || '1');

  if (!article) {
    return (
      <main role="main" className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-file-3-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
          <p className="text-foreground-600 mb-4">您访问的文章可能已被删除或不存在</p>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full font-medium text-sm cursor-pointer"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '西瓜视频',
    },
    url: `https://www.xigua-video.com/article/${article.id}`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.xigua-video.com/article/${article.id}`,
    },
  };

  const relatedArticles = getHotArticles().filter((a) => a.id !== article.id).slice(0, 3);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <main role="main">
        <Breadcrumb items={breadcrumbItems} />

        <article className="px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <header className="mb-8">
              <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500 mb-4">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.views} 次阅读
                </span>
              </div>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-3 py-1 rounded-full bg-accent-100 text-accent-700 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </header>

            <div className="mb-8 rounded-xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                title={`西瓜视频 - ${article.title}`}
                className="w-full aspect-video object-cover"
              />
            </div>

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <footer className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-foreground-600">
                    本文由 <strong className="text-foreground-900">{article.author}</strong> 发布于 西瓜视频
                  </p>
                  <p className="text-xs text-foreground-500 mt-1">
                    发布时间：{article.date}
                  </p>
                </div>
                <Link
                  to="/"
                  className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回首页
                </Link>
              </div>
            </footer>
          </div>
        </article>

        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
              相关文章
            </h2>
            <div className="flex flex-col gap-4">
              {relatedArticles.map((rel) => (
                <ArticleCard
                  key={rel.id}
                  id={rel.id}
                  title={rel.title}
                  excerpt={rel.excerpt}
                  image={rel.image}
                  date={rel.date}
                  author={rel.author}
                  views={rel.views}
                  tags={rel.tags}
                />
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}